import React, { FunctionComponent, useEffect, useState } from 'react'
import { Collection } from 'server'
import { App, Button, Divider, Form, Input, Modal, Popconfirm, Space, type TransferProps } from 'antd'
import { CloseOutlined, DeleteOutlined, RetweetOutlined, SaveOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { getAllData } from '../../../../utils/data'
import useQueryListener from '../../../../hooks/data/useQueryListener'
import { useFeathersClient } from '@strac/react-client-feathers'
import CollectionsTableTransfer from './CollectionsTableTransfer/CollectionsTableTransfer'
import { BatchCall } from 'feathers-batch'
import { BatchResults } from 'nx/src/tasks-runner/batch/batch-messages'

interface IProps {
  userId: number
  open: boolean
  setOpen: (value: boolean) => void
  selectedCollection?: Collection | undefined
  setSelectedCollection?: (value: Collection | undefined) => void
  initTargetKeys: number[]
}

const CollectionModal: FunctionComponent<any> = (props: IProps) => {
  const { selectedCollection, setSelectedCollection, open, setOpen, userId, initTargetKeys } = props

  const { notification } = App.useApp()

  const [form] = Form.useForm()

  const client = useFeathersClient()

  useEffect(() => {
    if (!selectedCollection) {
      form.resetFields()
    } else {
      form.setFieldsValue({
        collectionName: selectedCollection.name
      })
    }
  }, [selectedCollection, form])

  const collectionName = Form.useWatch('collectionName', form)

  const [targetKeys, setTargetKeys] = useState<TransferProps['targetKeys']>([])
  const [selectedKeys, setSelectedKeys] = useState<TransferProps['targetKeys']>([])

  const collectionDevicesQuery = {
    collection_id: selectedCollection?.id,
    active: true
  }

  const {
    data: collectionDevices,
    isLoading: collectionDevicesLoading,
    refetch
  } = useQuery({
    queryKey: ['collection-device', 'find', collectionDevicesQuery],
    queryFn: () => getAllData(client, 'collection-device', collectionDevicesQuery),
    enabled: !!selectedCollection
  })

  const collectionDeviceIds = collectionDevices?.map((cd: any) => cd.vehicle_id) || []

  useQueryListener('collection-device', refetch)

  useEffect(() => {
    const collectionDevicesIds = collectionDevices?.map((cd: any) => cd.vehicle_id) || []
    setTargetKeys((prevState: any) => {
      return [...initTargetKeys, ...collectionDevicesIds, ...prevState]
    })
  }, [collectionDevices, initTargetKeys, collectionDevicesLoading])

  const saveCollection = async () => {
    const calls: BatchCall[] = []
    try {
      if (targetKeys) {
        if (selectedCollection) {
          // Collection exists, update
          calls.push(['patch', 'collection', selectedCollection.id, { name: collectionName }])
          for (const collectionDevice of collectionDevices) {
            if (!targetKeys.includes(collectionDevice.vehicle_id)) {
              // Make device inactive
              calls.push(['patch', 'collection-device', collectionDevice.id, { active: false }])
            }
          }
          for (const vehicleId of targetKeys) {
            if (!collectionDeviceIds.includes(vehicleId)) {
              // Add device
              calls.push([
                'create',
                'collection-device',
                {
                  collection_id: selectedCollection.id,
                  vehicle_id: vehicleId
                }
              ])
            }
          }
        } else {
          // Collection does not exist, create
          const collection = await client
            .service('collection')
            .create({ name: collectionName, user_id: userId })
          for (const vehicleId of targetKeys) {
            calls.push([
              'create',
              'collection-device',
              { collection_id: collection.id, vehicle_id: vehicleId }
            ])
          }
        }

        client
          .service('batch')
          .create({ calls })
          .then((response: BatchResults) => {
            console.log('Batch response:', response)
            notification.success({
              message: 'Success',
              description: 'Collection was successfully updated'
            })
            setOpen(false)
            setSelectedCollection && setSelectedCollection(undefined)
          })
      } else {
        notification.error({
          message: 'Error',
          description: 'Please select at least one vehicle'
        })
      }
    } catch (e) {
      console.error(e)
      notification.error({
        message: 'Error',
        description: 'There was an error saving the collection'
      })
    }
  }

  const deleteCollection = async () => {
    const calls: BatchCall[] = []
    try {
      // Make collection inactive
      calls.push(['patch', 'collection', selectedCollection?.id, { active: false }])
      for (const collectionDevice of collectionDevices) {
        // Make device inactive
        calls.push(['patch', 'collection-device', collectionDevice.id, { active: false }])
      }
      client
        .service('batch')
        .create({ calls })
        .then((response: BatchResults) => {
          console.log('Batch response:', response)
          notification.success({
            message: 'Success',
            description: 'Collection was successfully removed'
          })
          setOpen(false)
          setSelectedCollection && setSelectedCollection(undefined)
        })
    } catch (e) {
      notification.error({
        message: 'Error',
        description: 'There was an error removing the collection'
      })
      console.error(e)
    }
  }

  const resetSelection = () => {
    const collectionDevicesIds = collectionDevices?.map((cd: any) => cd.vehicle_id) || []
    setTargetKeys([...collectionDevicesIds, ...(initTargetKeys || [])])
  }

  const handleCancel = () => {
    form.resetFields()
    setOpen(false)
    setSelectedCollection && setSelectedCollection(undefined)
    setTargetKeys([])
    setSelectedKeys([])
  }

  return (
    <Modal
      destroyOnClose={true}
      footer={null}
      height={'80vh'}
      width={'80vw'}
      centered={true}
      open={open}
      onCancel={handleCancel}
      closable={false}
    >
      <div style={{ display: 'flex', justifyContent: 'flex-end', height: '40px', marginBottom: '15px' }}>
        <Button
          onClick={handleCancel}
          type={'primary'}
          size={'large'}
          danger={true}
          icon={<CloseOutlined />}
        />
      </div>
      <Form layout={'vertical'} form={form}>
        <Space direction={'vertical'} style={{ width: '100%' }}>
          <Form.Item name={'collectionName'} style={{ margin: 0, width: '100%' }}>
            <Input autoFocus={true} size={'large'} allowClear={true} placeholder={'Collection name'} />
          </Form.Item>
        </Space>
      </Form>
      <Divider />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row'
        }}
      >
        <CollectionsTableTransfer
          targetKeys={targetKeys}
          setTargetKeys={setTargetKeys}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
        />
      </div>
      <Divider />
      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Popconfirm title={'Are you sure you want to delete this collection?'} onConfirm={deleteCollection}>
          <Button
            disabled={!selectedCollection}
            danger={true}
            ghost={true}
            type={'primary'}
            icon={<DeleteOutlined />}
            size={'large'}
          >
            Delete Collection
          </Button>
        </Popconfirm>
        <Space>
          <Button
            onClick={resetSelection}
            type={'primary'}
            ghost={true}
            icon={<RetweetOutlined />}
            size={'large'}
          >
            Reset Selection
          </Button>
          <Button
            disabled={!collectionName}
            onClick={saveCollection}
            type={'primary'}
            icon={<SaveOutlined />}
            size={'large'}
          >
            Save
          </Button>
        </Space>
      </div>
    </Modal>
  )
}

export default CollectionModal
